import { useEffect, useRef } from 'react';
import {
    useMap,
} from '@vis.gl/react-google-maps';
import { stopPropagation } from '../../../helpers/ObjectHelpers';
import MapService, { IMapService } from '../services/MapService';
import { IMapParamsService } from '../services/MapParamsService';
import {
    useSetMapZoom,
    useSetMapBounds,
    useSetSegmentIds,
    useSetSegmentGroupIds,
    useSetMapDragStart,
    useSetMapDragEnd,
    useSetMapResize,
    useSetDataClicked
} from '../hooks/useMapStore';

interface MapListenersProps {
    hasPilotageLoaded: boolean,
    hasShipPositionLoaded: boolean,
    paramsService: IMapParamsService,
}

export const MapListeners = ({
    hasPilotageLoaded,
    hasShipPositionLoaded,
    paramsService,
}: MapListenersProps) => {

    const map = useMap();

    const componentRef = useRef({
        hasBoundsChanged: false,
        skipBoundsChanged: false,
        hasPilotageLoaded: false,
        hasShipPositionLoaded: false,
        hasInitialized: false
    });
    const { current: localRef } = componentRef;
    const mapService: IMapService = new MapService();

    const setZoom = useSetMapZoom();
    const setBounds = useSetMapBounds();
    const setSegmentIds = useSetSegmentIds();
    const setSegmentGroupIds = useSetSegmentGroupIds();
    const setDragStart = useSetMapDragStart();
    const setDragEnd = useSetMapDragEnd();
    const setResize = useSetMapResize();
    const setDataClicked = useSetDataClicked();

    const initialize = () => {
        if (
            !map ||
            localRef.hasInitialized ||
            !localRef.hasPilotageLoaded ||
            !localRef.hasShipPositionLoaded
        ) return;
        
        const zoom = paramsService.zoom ?? 0;
        const lat = paramsService.lat ?? 0;
        const lng = paramsService.lng ?? 0;
        
        if (zoom !== 0 && lat !== 0 && lng !== 0) {
            map.setZoom(zoom);
            const latlng = new google.maps.LatLng(lat, lng);
            map.setCenter(latlng);
        }
        localRef.hasInitialized = true;
    }

    useEffect(() => {
        localRef.hasPilotageLoaded = hasPilotageLoaded;
        initialize();
    }, [hasPilotageLoaded]);

    useEffect(() => {
        localRef.hasShipPositionLoaded = hasShipPositionLoaded;
        initialize();
    }, [hasShipPositionLoaded]);

    useEffect(() => {

        if (!map) return;

        const handleBoundsChanged = () => {

            if (localRef.skipBoundsChanged) return;

            if (!localRef.hasBoundsChanged) {
                const changes = getChanges();
                setBounds(changes.bounds);
                localRef.hasBoundsChanged = true;
            } else {
                const changes = getChanges();
                setDragEnd(changes.bounds);
            }

            handleOnUpdateSearchParams();
        }

        const handleDragStart = () => {
            localRef.skipBoundsChanged = true;
            setDragStart();
        }

        const handleDragEnd = () => {
            localRef.skipBoundsChanged = true;

            const changes = getChanges();
            setDragEnd(changes.bounds);

            handleOnUpdateSearchParams();
        }

        const handleZoomChanged = () => {
            localRef.skipBoundsChanged = true;

            const changes = getChanges();
            setZoom(changes.zoom);

            handleOnUpdateSearchParams();
        }

        const handleResize = () => {
            localRef.skipBoundsChanged = true;

            const changes = getChanges();
            setResize(changes.bounds);
        }

        const handleOnClick = (e: any) => {
            stopPropagation(e);
            setDataClicked(e);
        }

        const getChanges = () => {
            const changes = mapService.getChangesWithinBounds(map, map!.data);

            setSegmentIds(changes.segmentIds);
            setSegmentGroupIds(changes.segmentGroupIds);

            return changes;
        }

        const handleOnUpdateSearchParams = () => {
            if (!localRef.hasPilotageLoaded || !localRef.hasShipPositionLoaded || !localRef.hasInitialized) return;
            if (!map) return;

            const zoom = map.getZoom();
            const center = map.getCenter();
            if (zoom && center) {
                paramsService.onZoomChange(zoom);
                paramsService.onCenterChange(center.lat(), center.lng());
            }
        }

        initialize();

        map.addListener("bounds_changed", handleBoundsChanged);
        map.addListener("dragstart", handleDragStart);
        map.addListener("dragend", handleDragEnd);
        map.addListener("zoom_changed", handleZoomChanged);
        map.addListener("resize", handleResize);

        map.data.addListener("click", handleOnClick);
        
        return (() => {
            window.google.maps.event.clearListeners(map, "bounds_changed");
            window.google.maps.event.clearListeners(map, "dragend");
            window.google.maps.event.clearListeners(map, "zoom_changed");
            window.google.maps.event.clearListeners(map, "resize");

            window.google.maps.event.clearListeners(map, "click");
        });

    
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [map]);

    return null;
}
